import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    grecaptcha?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => number;
      reset: (id?: number) => void;
      ready?: (cb: () => void) => void;
    };
  }
}

const SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';
let scriptPromise: Promise<void> | null = null;

function loadRecaptchaScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    if (window.grecaptcha?.render) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('reCAPTCHA failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('reCAPTCHA failed to load'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export interface ReCaptchaProps {
  /** Called with the token on success, or null when expired / errored. */
  onVerify: (token: string | null) => void;
}

/**
 * Google reCAPTCHA v2 checkbox widget.
 *
 * Renders nothing when `VITE_RECAPTCHA_SITE_KEY` is not configured — in that case
 * the backend also skips verification (relying on the honeypot + rate limit), so
 * the form still works in dev / before keys are set. No external npm dependency:
 * the Google api.js script is loaded on demand and the widget rendered explicitly.
 */
export default function ReCaptcha({ onVerify }: ReCaptchaProps) {
  const siteKey = (import.meta as any).env?.VITE_RECAPTCHA_SITE_KEY as string | undefined;
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;

    loadRecaptchaScript()
      .then(() => {
        const tryRender = () => {
          if (cancelled || widgetIdRef.current !== null) return;
          const g = window.grecaptcha;
          if (!g?.render || !containerRef.current) {
            setTimeout(tryRender, 150);
            return;
          }
          widgetIdRef.current = g.render(containerRef.current, {
            sitekey: siteKey,
            callback: (token: string) => onVerify(token),
            'expired-callback': () => onVerify(null),
            'error-callback': () => onVerify(null),
          });
        };
        const g = window.grecaptcha;
        if (g?.ready) g.ready(tryRender);
        else tryRender();
      })
      .catch(() => onVerify(null));

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={containerRef} />;
}
